<template>
	<div id="how-works-page" data-aos="fade">
		<!-- Hero -->
		<section class="top-section">
			<div class="container">
				<div class="row">
					<div class="col-12 col-lg-10 col-xl-8 mx-auto text-center">
						<img
							src="public/images/pages/how-works/books.png"
							alt=""
							class="hero-image" 
							data-aos="fade-up"
						>
						<h1 class="hero-title" data-aos="fade-up" data-aos-delay="100">
							La plataforma <span class="text-accent">legal del futuro</span>
						</h1>
						<p class="hero-lead" data-aos="fade-up" data-aos-delay="200">
							En un ecosistema legal lleno de obstáculos, que a veces se siente como un laberinto, UNLAWK nace para
							transformar la confusión en certidumbre, teniendo claro algo: no buscamos cambiar las leyes, sino
							desbloquear el camino hacia ellas.
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- ¿Por qué es diferente? -->
		<section class="difference-section">
			<div class="container">
				<div class="row mb-4 mb-md-5">
					<div class="col-12 text-center" data-aos="fade-up">
						<h2 class="section-title">¿Por qué LEXFLOW es diferente?</h2>
						<p class="section-lead">
							Transformamos el proceso legal tradicional con tecnología de vanguardia
						</p>
					</div>
				</div>

				<div class="row">
					<div
						v-for="feature in features"
						:key="feature.id"
						class="col-md-4 mb-4 mb-md-0"
						data-aos="fade-up" :data-aos-delay="feature.aosDelay"
					>
						<div class="feature-card h-100" :class="feature.variant">
							<div class="feature-icon" :class="feature.iconVariant">
								<i :class="feature.icon"></i>
							</div>
							<h3 class="feature-title" :class="feature.titleClass">{{ feature.title }}</h3>
							<p class="feature-text" :class="feature.textClass">{{ feature.text }}</p>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Proceso paso a paso -->
		<section class="process-section">
			<div class="container">
				<div class="row mb-4 mb-md-5" data-aos="fade-up">
					<div class="col-12 text-center">
						<h2 class="section-title">El proceso LEXFLOW paso a paso</h2>
						<p class="section-lead section-lead--lg">
							Un recorrido guiado desde tu primera consulta hasta la solución de tu problema legal
						</p>
					</div>
				</div>

				<div
					v-for="(step, index) in steps"
					:key="step.id"
					class="row align-items-center process-step flex-column"
					:class="index % 2 === 1 ? 'flex-md-row-reverse' : 'flex-md-row'"
				>
					<div class="col-auto mb-3 mb-md-0" :data-aos="step.aosMove">
						<div class="step-badge" :class="step.badgeVariant">
							<i :class="[step.icon, step.badgeIconClass]"></i>
							<span :class="step.badgeLabelClass">Paso {{ index + 1 }}</span>
						</div>
					</div>
					<div class="col" :data-aos="step.aosMove" data-aos-delay="150">
						<div class="step-card" :class="step.cardVariant">
							<h3 class="step-title">{{ step.title }}</h3>
							<p class="step-text">{{ step.text }}</p>
							<ul class="step-list list-unstyled mb-0">
								<li
									v-for="(item, itemIndex) in step.items"
									:key="itemIndex"
									class="d-flex align-items-start"
								>
									<i
										class="fa-regular fa-circle-check flex-shrink-0 mt-1 mr-2"
										:class="step.checkClass"
									></i>
									<span>{{ item }}</span>
								</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</section>

		<!-- Comparación -->
		<section class="comparison-section">
			<div class="container">
				<div class="row mb-4 mb-md-5">
					<div class="col-12 col-lg-10 mx-auto text-center" data-aos="fade-up">
						<h2 class="comparison-title">Comparación: método tradicional vs. LEXFLOW</h2>
						<p class="comparison-lead">
							Ve la diferencia real en tiempo y esfuerzo para obtener asesoría legal de calidad
						</p>
					</div>
				</div>

				<div class="row">
					<div class="col-12 col-lg-10 mx-auto">
						<!-- Sin unlawk -->
						<div class="compare-card mb-4" data-aos="fade-up">
							<div class="d-flex align-items-center justify-content-between mb-3">
								<h3 class="compare-heading mb-0">Sin unlawk</h3>
								<span class="compare-time compare-time--slow">30 días</span>
							</div>
							<div class="compare-timeline-labels d-flex justify-content-between mb-2">
								<span>Día 1</span>
								<span>Día 15</span>
								<span>Día 30</span>
							</div>
							<div class="compare-bar compare-bar--slow">
								<div class="compare-bar-fill compare-bar-fill--slow"></div>
								<div class="compare-bar-labels d-flex align-items-center justify-content-between">
									<span>Búsqueda de abogado</span>
									<span class="d-none d-md-inline">Citas, consultas y MUCHO rollo.....</span>
									<span>Respuesta final</span>
								</div>
							</div>
						</div>

						<!-- Con unlawk -->
						<div class="compare-card" data-aos="fade-up" data-aos-delay="350">
							<div class="d-flex align-items-center justify-content-between mb-3">
								<h3 class="compare-heading mb-0">Con unlawk</h3>
								<span class="compare-time compare-time--fast">1 a 3 días</span>
							</div>
							<div class="compare-timeline-labels d-flex justify-content-between mb-2">
								<span>Día 1</span>
								<span>Día 2</span>
								<span>Día 3</span>
							</div>
							<div class="row no-gutters compare-fast-grid">
								<div class="col-12 col-md-4 mb-2 mb-md-0 pr-md-2">
									<div class="compare-day compare-day--navy">
										<i class="fa-regular fa-robot"></i>
										<span>Consulta con Lexi IA</span>
									</div>
								</div>
								<div class="col-12 col-md-4 mb-2 mb-md-0 px-md-1">
									<div class="compare-day compare-day--mint">
										<i class="fa-regular fa-users"></i>
										<span>Te sugerimos diversas firmas legales y/o abogados independientes</span>
									</div>
								</div>
								<div class="col-12 col-md-4 pl-md-2">
									<div class="compare-day compare-day--dark">
										<i class="fa-regular fa-circle-check"></i>
										<span>Seleccionas el especialista de tu preferencia y obtienes una consulta útil</span>
									</div>
								</div>
							</div>
						</div>

						<p class="comparison-footnote text-center mt-4 mb-0" data-aos="fade-up" data-aos-delay="100">
							<span class="comparison-footnote-accent">10x más rápido</span> que los métodos tradicionales
						</p>
					</div>
				</div>
			</div>
		</section>

		<!-- CTA -->
		<section class="cta-section">
			<div class="container">
				<div class="row">
					<div class="col-12 col-lg-8 mx-auto text-center">
						<h2 class="cta-title" data-aos="fade-up">Tu problema legal merece una solución moderna</h2>
						<p class="cta-lead" data-aos="fade-up" data-aos-delay="100">
							Miles de personas ya han resuelto sus casos con LEXFLOW. Únete a ellos y experimenta la diferencia de una
							plataforma legal diseñada para el siglo XXI.
						</p>
						<div class="d-block" data-aos="fade-up" data-aos-delay="200">
							<b-button
								to="/chat"
								variant="danger"
								size="lg"
								class="cta-btn"
							>
								<i class="fa-regular fa-robot mr-2"></i>
								Comenzar consulta gratuita
							</b-button>
						</div>
						<p class="cta-note" data-aos="fade-up" data-aos-delay="300">Sin tarjeta de crédito · Sin compromisos · Respuesta en minutos</p>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	data() {
		return {
			features: [
				{
					id: 'speed',
					aosDelay: 0,
					icon: 'fa-regular fa-bolt',
					iconVariant: 'feature-icon--light',
					variant: 'feature-card--navy',
					titleClass: 'feature-title--light',
					textClass: 'feature-text--light',
					title: 'Velocidad sin precedentes',
					text: 'Reduce el tiempo de espera de semanas a días. Nuestra IA procesa tu caso instantáneamente y te conecta con el especialista correcto en minutos.',
				},
				{
					id: 'verified',
					aosDelay: 100,
					icon: 'fa-regular fa-shield',
					iconVariant: 'feature-icon--navy',
					variant: 'feature-card--mint',
					titleClass: 'feature-title--dark',
					textClass: 'feature-text--navy',
					title: 'Profesionales verificados',
					text: 'Todos nuestros abogados y firmas pasan por un riguroso proceso de verificación. Cédulas profesionales validadas y años de experiencia comprobable.',
				},
				{
					id: 'match',
					aosDelay: 200,
					icon: 'fa-regular fa-star',
					iconVariant: 'feature-icon--light',
					variant: 'feature-card--primary',
					titleClass: 'feature-title--light',
					textClass: 'feature-text--light',
					title: 'Match perfecto',
					text: 'Lexi analiza tu caso y te recomienda los especialistas con mayor expertise en tu área específica, basándose en miles de casos similares.',
				},
			],
			steps: [
				{
					id: 'lexi',
					aosMove: 'fade-right',
					icon: 'fa-regular fa-robot',
					badgeVariant: 'step-badge--primary',
					badgeIconClass: '',
					badgeLabelClass: '',
					cardVariant: 'step-card--primary',
					checkClass: 'text-primary-accent',
					title: 'Consulta con Lexi, tu asistente legal IA',
					text: 'Inicia una conversación natural con Lexi. Describe tu situación legal en tus propias palabras: contratos, disputas laborales, temas familiares, lo que necesites. Lexi hará preguntas inteligentes para entender mejor tu caso.',
					items: [
						'Disponible 24/7, sin necesidad de agendar citas',
						'Análisis inicial gratuito de tu situación',
						'Identificación inmediata del área legal y urgencia',
					],
				},
				{
					id: 'expediente',
					aosMove: 'fade-left',
					icon: 'fa-regular fa-file-lines',
					badgeVariant: 'step-badge--mint',
					badgeIconClass: 'step-badge-icon--navy',
					badgeLabelClass: 'step-badge-label--navy',
					cardVariant: 'step-card--mint',
					checkClass: 'text-navy',
					title: 'Crea tu expediente digital completo',
					text: 'Completa un formulario inteligente diseñado específicamente para tu tipo de caso. Sube documentos relevantes: contratos, correos, fotos, lo que tengas. Tu expediente se organiza automáticamente.',
					items: [
						'Formularios adaptativos según tu tipo de caso',
						'Almacenamiento seguro con encriptación de documentos',
						'Acceso permanente a tu expediente desde cualquier dispositivo',
					],
				},
				{
					id: 'match',
					aosMove: 'fade-right',
					icon: 'fa-regular fa-users',
					badgeVariant: 'step-badge--navy',
					badgeIconClass: '',
					badgeLabelClass: '',
					cardVariant: 'step-card--navy',
					checkClass: 'text-navy',
					title: 'Conecta con el especialista perfecto',
					text: 'Recibe recomendaciones personalizadas de abogados y firmas especializadas en tu caso específico. Revisa perfiles detallados, tarifas transparentes, reseñas reales de clientes, y elige el que mejor se ajuste a ti.',
					items: [
						'Match IA basado en especialidad, experiencia y ubicación',
						'Comparación directa de honorarios y disponibilidad',
						'Tu expediente se comparte automáticamente con el abogado elegido',
					],
				},
			],
		}
	},
}
</script>
